import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { ProgramApi } from "@/api/program.api";
import { TransactionApi } from "@/api/transaction.api";

import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Search,
  RotateCcw,
  CalendarDays,
  Filter,
  Download,
  ChevronDown,
  FileText,
  Loader2,
} from "lucide-react";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  startOfDay,
  endOfDay,
  startOfMonth,
  endOfMonth,
  subDays,
  subMonths,
} from "date-fns";
import { EnhancedPaginationControl } from "@/components/common/EnhancedPaginationControl";
import { getFormattedDateTime } from "@/utils";
import { TransactionTableSkeleton } from "@/components/transactions/TransactionTableSkeleton";
import { DatePickerWithRange } from "@/components/ui/date-range-picker";
import { DateRange } from "react-day-picker";
import { useDebounce } from "@/hooks/use-debounce";
import { downloadResponseFile } from "@/utils/common.utils";

const ROWS_OPTIONS = [5, 10, 20, 50];

function getDateRangeMillisPreset(
  range: string
): { startDate?: number; endDate?: number } | {} {
  const now = new Date();
  switch (range) {
    case "today":
      return {
        startDate: startOfDay(now).getTime(),
        endDate: endOfDay(now).getTime(),
      };
    case "yesterday": {
      const y = subDays(now, 1);
      return {
        startDate: startOfDay(y).getTime(),
        endDate: endOfDay(y).getTime(),
      };
    }
    case "7days":
      return {
        startDate: startOfDay(subDays(now, 6)).getTime(),
        endDate: endOfDay(now).getTime(),
      };
    case "30days":
      return {
        startDate: startOfDay(subDays(now, 29)).getTime(),
        endDate: endOfDay(now).getTime(),
      };
    case "90days":
      return {
        startDate: startOfDay(subDays(now, 89)).getTime(),
        endDate: endOfDay(now).getTime(),
      };
    case "thisMonth":
      return {
        startDate: startOfMonth(now).getTime(),
        endDate: endOfMonth(now).getTime(),
      };
    case "lastMonth":
      return {
        startDate: startOfMonth(subMonths(now, 1)).getTime(),
        endDate: endOfMonth(subMonths(now, 1)).getTime(),
      };
    case "all":
    default:
      return {};
  }
}

function customDateRangeToMillis(startDate: string, endDate: string) {
  return {
    startDate: startOfDay(new Date(startDate)).getTime(),
    endDate: endOfDay(new Date(endDate)).getTime(),
  };
}

const XpandAccountingEntry = () => {
  const [entries, setEntries] = useState<any[]>([]);
  const [filteredEntries, setFilteredEntries] = useState<any[]>([]);
  const [programs, setPrograms] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProgram, setSelectedProgram] = useState("all");
  const [dateRange, setDateRange] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [totalEntries, setTotalEntries] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>(
    undefined
  );
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);
  const [activeCategory, setActiveCategory] = useState("amount");
  const [exportPopoverOpen, setExportPopoverOpen] = useState(false);
  const [isExportingCSV, setIsExportingCSV] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [amount, setAmount] = useState("");
  const [pendingAmount, setPendingAmount] = useState("");
  const [invoiceId, setInvoiceId] = useState("");
  const [pendingInvoiceId, setPendingInvoiceId] = useState("");
  const [accountName, setAccountName] = useState("");
  const [pendingAccountName, setPendingAccountName] = useState("");
  const [accountType, setAccountType] = useState("");
  const [pendingAccountType, setPendingAccountType] = useState("");
  const [entryType, setEntryType] = useState("");
  const [pendingEntryType, setPendingEntryType] = useState("");
  const [pendingProgram, setPendingProgram] = useState("all");
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);

  // Highlight Filter button when advanced filters are applied (same as other pages)
  const activeFilterCount = [
    selectedProgram !== "all",
    Boolean(amount.trim()),
    Boolean(invoiceId.trim()),
    Boolean(accountName.trim()),
    Boolean(accountType.trim()),
    Boolean(entryType.trim()),
  ].filter(Boolean).length;

  // Add debounced search term
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Pagination
  const totalPages = Math.ceil(totalEntries / rowsPerPage);

  // Sync pending states with current states when filter popover opens
  useEffect(() => {
    if (filterPopoverOpen) {
      setPendingProgram(selectedProgram);
      setPendingAccountName(accountName);
      setPendingAccountType(accountType);
      setPendingEntryType(entryType);
    }
  }, [filterPopoverOpen, selectedProgram, accountName, accountType, entryType]);

  useEffect(() => {
    ProgramApi.getProgramsFromDB({
      pageNumber: 0,
      pageSize: 100,
      sortOrder: "desc",
      sortBy: "createdDate",
      programCode: null,
      programName: null,
      programType: "TRANSFER",
    }).then((res: any) => {
      setPrograms(
        Array.isArray(res.data?.programVOs) ? res.data.programVOs : []
      );
    });
  }, []);

  useEffect(() => {
    fetchEntries();
    // eslint-disable-next-line
  }, [
    page,
    rowsPerPage,
    selectedProgram,
    debouncedSearchTerm,
    startDate,
    endDate,
    amount,
    invoiceId,
    accountName,
    accountType,
    entryType,
  ]);

  const fetchEntries = async () => {
    setIsLoading(true);
    try {
      let payload: any = {
        pageNumber: page,
        pageSize: rowsPerPage,
        sortOrder: "desc",
        sortBy: "createdDate",
        transactionType: "exchange",
        programCode: selectedProgram !== "all" ? selectedProgram : null,
        transferTransactionId: debouncedSearchTerm.trim()
          ? debouncedSearchTerm.trim()
          : null,
        amount: amount.trim() ? Number(amount.trim()) : undefined,
        invoiceId: invoiceId.trim() ? invoiceId.trim() : undefined,
        accountName: accountName.trim() ? accountName.trim() : undefined,
        accountType: accountType.trim() ? accountType.trim() : undefined,
        entryType: entryType.trim() ? entryType.trim() : undefined,
      };
      if (dateRange === "custom" && startDate && endDate) {
        const customRange = customDateRangeToMillis(startDate, endDate);
        payload.startDate = customRange.startDate;
        payload.endDate = customRange.endDate;
      } else if (dateRange) {
        payload = { ...payload, ...getDateRangeMillisPreset(dateRange) };
      }
      const res = await TransactionApi.getAccountingEntries(payload);
      const data = Array.isArray(res.data?.accountingEntryVOs)
        ? res.data.accountingEntryVOs
        : [];
      // Map API response to table row structure
      const mapped = data.map((entry: any) => ({
        txId: entry.transferTransactionId || "",
        date: getFormattedDateTime(entry.createdDate),
        program: entry.transferTransactionVO?.programVO?.programName || "",
        merchant: entry.transferPartnerVO?.partnerName || "",
        accountCode: entry.accountVO?.accountCode || "",
        accountName: entry.accountVO?.accountName || "",
        accountType: entry.accountVO?.accountType || "",
        amount: entry.amount,
        currency: entry.currency,
        invoiceId: entry.invoiceId || "",
        entryType: entry.entryType || "",
      }));
      setEntries(mapped);
      setFilteredEntries(mapped);
      setTotalEntries(Number(res.data?.totalRecords) || mapped.length);
    } catch (e) {
      setEntries([]);
      setFilteredEntries([]);
      setTotalEntries(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSearchTerm("");
    setSelectedProgram("all");
    setDateRange("");
    setPage(0);
    setCustomDateRange(undefined);
    setStartDate(undefined);
    setEndDate(undefined);
    setAmount("");
    setPendingAmount("");
    setInvoiceId("");
    setPendingInvoiceId("");
    setAccountName("");
    setPendingAccountName("");
    setAccountType("");
    setPendingAccountType("");
    setEntryType("");
    setPendingEntryType("");
    setPendingProgram("all");
    toast.success("Filters reset");
  };

  const handleExport = async (format: "csv" | "pdf") => {
    if (format === "csv") {
      setIsExportingCSV(true);
      try {
        const API_BASE = import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL;
        let payload: any = {
          sortOrder: "desc",
          sortBy: "createdDate",
          transactionType: "exchange",
          programCode: selectedProgram !== "all" ? selectedProgram : null,
          transferTransactionId: debouncedSearchTerm.trim()
            ? debouncedSearchTerm.trim()
            : null,
          amount: amount.trim() ? Number(amount.trim()) : undefined,
          invoiceId: invoiceId.trim() ? invoiceId.trim() : undefined,
          accountName: accountName.trim() ? accountName.trim() : undefined,
          accountType: accountType.trim() ? accountType.trim() : undefined,
          entryType: entryType.trim() ? entryType.trim() : undefined,
        };
        if (dateRange === "custom" && startDate && endDate) {
          const customRange = customDateRangeToMillis(startDate, endDate);
          payload.startDate = customRange.startDate;
          payload.endDate = customRange.endDate;
        } else if (dateRange) {
          payload = { ...payload, ...getDateRangeMillisPreset(dateRange) };
        }

        const response = await fetch(
          `${API_BASE}/accounting-entry/getAccountingEntryCSVFormat`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            credentials: "include",
          }
        );

        if (!response.ok) {
          throw new Error("Failed to export CSV");
        }

        const filename = await downloadResponseFile(
          response,
          `xpand_accounting_entries_${
            new Date().toISOString().split("T")[0]
          }.csv`,
        );
        toast.success(
          `Xpand accounting entries exported as ${filename} successfully!`,
        );
      } catch (error) {
        console.error("Error exporting CSV:", error);
        toast.error("Failed to export Xpand accounting entries as CSV");
      } finally {
        setIsExportingCSV(false);
        setExportPopoverOpen(false);
      }
    } else if (format === "pdf") {
      setIsExportingPDF(true);
      try {
        const API_BASE = import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL;
        let payload: any = {
          sortOrder: "desc",
          sortBy: "createdDate",
          transactionType: "exchange",
          programCode: selectedProgram !== "all" ? selectedProgram : null,
          transferTransactionId: debouncedSearchTerm.trim()
            ? debouncedSearchTerm.trim()
            : null,
          amount: amount.trim() ? Number(amount.trim()) : undefined,
          invoiceId: invoiceId.trim() ? invoiceId.trim() : undefined,
          accountName: accountName.trim() ? accountName.trim() : undefined,
          accountType: accountType.trim() ? accountType.trim() : undefined,
          entryType: entryType.trim() ? entryType.trim() : undefined,
        };
        if (dateRange === "custom" && startDate && endDate) {
          const customRange = customDateRangeToMillis(startDate, endDate);
          payload.startDate = customRange.startDate;
          payload.endDate = customRange.endDate;
        } else if (dateRange) {
          payload = { ...payload, ...getDateRangeMillisPreset(dateRange) };
        }

        const response = await fetch(
          `${API_BASE}/accounting-entry/generateAccountingEntriesPdfFormat`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            credentials: "include",
          }
        );

        if (!response.ok) {
          throw new Error("Failed to export PDF");
        }

        const filename = await downloadResponseFile(
          response,
          `xpand_accounting_entries_${
            new Date().toISOString().split("T")[0]
          }.pdf`,
        );
        toast.success(
          `Xpand accounting entries exported as ${filename} successfully!`,
        );
      } catch (error) {
        console.error("Error exporting PDF:", error);
        toast.error("Failed to export Xpand accounting entries as PDF");
      } finally {
        setIsExportingPDF(false);
        setExportPopoverOpen(false);
      }
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Accounting Entries</h1>
        <p className="text-gray-500 mt-1">
          View accounting entries for loyalty programs
        </p>
      </div>
      {/* Filter section */}
      <div className="flex flex-col sm:flex-row gap-4 items-end sm:items-center bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
        <div className="flex-1 flex gap-2 items-end">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
              <Input
                id="search"
                placeholder="Search by transaction ID..."
                className="pl-9"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="flex gap-2 items-center">
          <div className="w-64 h-10 flex items-center">
            <DatePickerWithRange
              date={customDateRange}
              setDate={(range) => {
                setCustomDateRange(range);
                if (range?.from && range?.to) {
                  setDateRange("custom");
                  setStartDate(startOfDay(range.from).toISOString());
                  setEndDate(endOfDay(range.to).toISOString());
                }
              }}
              placeholder="Custom Date Range"
            />
          </div>
          <Popover
            open={filterPopoverOpen}
            onOpenChange={(open) => {
              setFilterPopoverOpen(open);
              if (open) {
                setPendingInvoiceId(invoiceId);
              }
            }}
          >
            <PopoverTrigger asChild>
              <Button
                variant={activeFilterCount > 0 ? "default" : "outline"}
                size="sm"
                className="flex items-center gap-2 px-3 py-2 text-sm"
                onClick={() => setFilterPopoverOpen(true)}
              >
                <Filter className="h-4 w-4" />
                Filter
                {activeFilterCount > 0 && (
                  <span className="ml-1 bg-blue-600 text-white rounded-full px-2 py-0.5 text-xs font-semibold">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[340px] p-0" align="end">
              <div className="flex max-h-[320px] h-[320px]">
                <div className="w-32 border-r bg-gray-50 p-2 flex flex-col gap-1">
                  {[
                    { label: "Transfer Partner", value: "program" },
                    { label: "Amount", value: "amount" },
                    { label: "Invoice ID", value: "invoiceId" },
                    { label: "Account Name", value: "accountName" },
                    { label: "Account Type", value: "accountType" },
                    { label: "Entry Type", value: "entryType" },
                  ].map((cat) => (
                    <button
                      key={cat.value}
                      className={`text-left px-2 py-1 rounded hover:bg-gray-100 font-medium text-xs ${
                        activeCategory === cat.value
                          ? "bg-white shadow text-blue-600"
                          : "text-gray-700"
                      }`}
                      onClick={() => setActiveCategory(cat.value)}
                    >
                      {cat.label}
                    </button>
                  ))}
                  <div className="flex-1" />
                  <button
                    className="text-[10px] text-gray-500 hover:underline mt-2"
                    onClick={() => {
                      // Reset all filter fields
                      setSelectedProgram("all");
                      setAmount("");
                      setInvoiceId("");
                      setAccountName("");
                      setAccountType("");
                      setEntryType("");
                    }}
                    type="button"
                  >
                    Clear all
                  </button>
                </div>
                <div className="flex-1 p-2 flex flex-col min-h-0">
                  {/* Render filter fields based on activeCategory */}
                  {activeCategory === "program" && (
                    <div className="flex flex-col gap-2">
                      <div className="max-h-60 overflow-y-auto">
                        {programs.map((program: any) => (
                          <label
                            key={program.programCode}
                            className="flex items-center gap-2 cursor-pointer py-2"
                          >
                            <input
                              type="radio"
                              name="program-filter"
                              checked={pendingProgram === program.programCode}
                              onChange={() =>
                                setPendingProgram(program.programCode)
                              }
                              className="form-radio h-4 w-4 text-blue-600"
                            />
                            <span className="text-xs font-medium">
                              {program.programName}
                            </span>
                          </label>
                        ))}
                      </div>
                      <div className="flex justify-end gap-2 mt-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs px-3 py-1 h-7"
                          onClick={() => {
                            setPendingProgram(selectedProgram);
                            setFilterPopoverOpen(false);
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          className="text-xs px-3 py-1 h-7"
                          onClick={() => {
                            setSelectedProgram(pendingProgram);
                            setPage(0);
                            setFilterPopoverOpen(false);
                          }}
                        >
                          Apply
                        </Button>
                      </div>
                    </div>
                  )}
                  {activeCategory === "amount" && (
                    <div className="flex flex-col gap-2">
                      <input
                        type="number"
                        placeholder="Enter amount"
                        value={pendingAmount}
                        onChange={(e) => setPendingAmount(e.target.value)}
                        className="h-10 w-full border rounded px-2 text-sm"
                      />
                      <div className="flex justify-end gap-2 mt-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs px-3 py-1 h-7"
                          onClick={() => {
                            setPendingAmount(amount);
                            setFilterPopoverOpen(false);
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          className="text-xs px-3 py-1 h-7"
                          onClick={() => {
                            setAmount(pendingAmount);
                            setPage(0);
                            setFilterPopoverOpen(false);
                          }}
                        >
                          Apply
                        </Button>
                      </div>
                    </div>
                  )}
                  {activeCategory === "invoiceId" && (
                    <div className="flex flex-col gap-2">
                      <input
                        type="text"
                        placeholder="Enter Invoice ID"
                        value={pendingInvoiceId}
                        onChange={(e) => setPendingInvoiceId(e.target.value)}
                        className="h-10 w-full border rounded px-2 text-sm"
                      />
                      <div className="flex justify-end gap-2 mt-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs px-3 py-1 h-7"
                          onClick={() => {
                            setPendingInvoiceId(invoiceId);
                            setFilterPopoverOpen(false);
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          className="text-xs px-3 py-1 h-7"
                          onClick={() => {
                            setInvoiceId(pendingInvoiceId);
                            setPage(0);
                            setFilterPopoverOpen(false);
                          }}
                        >
                          Apply
                        </Button>
                      </div>
                    </div>
                  )}
                  {activeCategory === "dateRange" && (
                    <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
                      {[
                        { value: "30days", label: "Last 30 Days" },
                        { value: "7days", label: "Last 7 Days" },
                        { value: "today", label: "Today" },
                        { value: "yesterday", label: "Yesterday" },
                        { value: "thisMonth", label: "This Month" },
                        { value: "lastMonth", label: "Last Month" },
                      ].map((opt) => (
                        <label
                          key={opt.value}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <input
                            type="radio"
                            name="dateRange-filter"
                            checked={dateRange === opt.value}
                            onChange={() => setDateRange(opt.value)}
                            className="form-radio h-4 w-4 text-blue-600"
                          />
                          <span className="text-xs font-medium">
                            {opt.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                  {activeCategory === "accountName" && (
                    <div className="flex flex-col gap-2">
                      <input
                        type="text"
                        placeholder="Enter Account Name"
                        value={pendingAccountName}
                        onChange={(e) => setPendingAccountName(e.target.value)}
                        className="h-10 w-full border rounded px-2 text-sm"
                      />
                      <div className="flex justify-end gap-2 mt-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs px-3 py-1 h-7"
                          onClick={() => {
                            setPendingAccountName(accountName);
                            setFilterPopoverOpen(false);
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          className="text-xs px-3 py-1 h-7"
                          onClick={() => {
                            setAccountName(pendingAccountName);
                            setPage(0);
                            setFilterPopoverOpen(false);
                          }}
                        >
                          Apply
                        </Button>
                      </div>
                    </div>
                  )}
                  {activeCategory === "accountType" && (
                    <div className="flex flex-col gap-2">
                      <select
                        value={pendingAccountType}
                        onChange={(e) => setPendingAccountType(e.target.value)}
                        className="h-10 w-full border rounded px-2 text-sm"
                      >
                        <option value="">Select Account Type</option>
                        <option value="ASSET">Asset</option>
                        <option value="LIABILITY">Liability</option>
                        <option value="EQUITY">Equity</option>
                      </select>
                      <div className="flex justify-end gap-2 mt-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs px-3 py-1 h-7"
                          onClick={() => {
                            setPendingAccountType(accountType);
                            setFilterPopoverOpen(false);
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          className="text-xs px-3 py-1 h-7"
                          onClick={() => {
                            setAccountType(pendingAccountType);
                            setPage(0);
                            setFilterPopoverOpen(false);
                          }}
                        >
                          Apply
                        </Button>
                      </div>
                    </div>
                  )}
                  {activeCategory === "entryType" && (
                    <div className="flex flex-col gap-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="entryType-filter-xpand"
                          checked={pendingEntryType === "DEBIT"}
                          onChange={() => setPendingEntryType("DEBIT")}
                          className="form-radio h-4 w-4 text-blue-600"
                        />
                        <span className="text-xs font-medium">Debit</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="entryType-filter-xpand"
                          checked={pendingEntryType === "CREDIT"}
                          onChange={() => setPendingEntryType("CREDIT")}
                          className="form-radio h-4 w-4 text-blue-600"
                        />
                        <span className="text-xs font-medium">Credit</span>
                      </label>
                      <div className="flex justify-end gap-2 mt-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs px-3 py-1 h-7"
                          onClick={() => {
                            setPendingEntryType(entryType);
                            setFilterPopoverOpen(false);
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          className="text-xs px-3 py-1 h-7"
                          onClick={() => {
                            setEntryType(pendingEntryType);
                            setPage(0);
                            setFilterPopoverOpen(false);
                          }}
                        >
                          Apply
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </PopoverContent>
          </Popover>
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2 px-3 py-2 text-sm"
            onClick={handleReset}
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </Button>
          <Popover open={exportPopoverOpen} onOpenChange={setExportPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-2 px-3 py-2 text-sm"
                disabled={isExportingCSV || isExportingPDF}
              >
                <Download className="h-4 w-4" />
                Export
                <ChevronDown className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-2" align="end">
              <div className="flex flex-col gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="justify-start text-sm"
                  onClick={() => handleExport("csv")}
                  disabled={isExportingCSV || isExportingPDF}
                >
                  {isExportingCSV ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <FileText className="h-4 w-4 mr-2" />
                  )}
                  <span>CSV</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="justify-start text-sm"
                  onClick={() => handleExport("pdf")}
                  disabled={isExportingCSV || isExportingPDF}
                >
                  {isExportingPDF ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <FileText className="h-4 w-4 mr-2" />
                  )}
                  <span>PDF</span>
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
      {/* Data Table */}
      {isLoading ? (
        <TransactionTableSkeleton rowCount={rowsPerPage} />
      ) : (
        <DataTable
          title="Accounting Entries"
          data={filteredEntries}
          columns={[
            { header: "Txn ID", accessorKey: "txId" },
            { header: "Date", accessorKey: "date" },
            {
              header: "Partner",
              accessorKey: "program",
              cell: (row: any) => {
                if (!row.program) return "";
                const words = row.program.split(" ");
                const display =
                  words.length > 2
                    ? `${words.slice(0, 2).join(" ")}...`
                    : row.program;
                return <span title={row.program}>{display}</span>;
              },
            },
            { header: "Program", accessorKey: "merchant" },
            { header: "Account Code", accessorKey: "accountCode" },
            {
              header: "Account Name / Type",
              id: "accountNameType",
              cell: (row: any) => (
                <div>
                  <div>{row.accountName}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {row.accountType}
                  </div>
                </div>
              ),
            },
            {
              header: "Amount",
              id: "amountCurrency",
              cell: (row: any) => {
                // // Parse amount and format to exactly 2 decimal places
                // const formattedAmount = parseFloat(row.amount || 0).toFixed(2);
                // // Debug: Log original and formatted values
                // console.log(
                //   `Original amount: ${row.amount}, Formatted: ${formattedAmount}`
                // );
                const amount = parseFloat(row.amount || 0);
                const truncated = Math.floor(amount * 100) / 100;
                const formattedAmount =
                  truncated % 1 === 0
                    ? truncated.toString()
                    : truncated.toString().replace(/(\.\d*?)0+$/, "$1");

                console.log(
                  `Original amount: ${row.amount}, Formatted: ${formattedAmount}`
                );
                return (
                  <span className="inline-flex items-center justify-center rounded-full text-xs font-semibold w-16 h-10">
                    <span className="inline-block p-1">{formattedAmount}</span>
                    <span>{row.currency}</span>
                  </span>
                );
              },
            },
            { header: "Invoice ID", accessorKey: "invoiceId" },
            { header: "Entry Type", accessorKey: "entryType" },
          ]}
          customTableBody={undefined}
        />
      )}
      <style>{`
        .card-modern table tbody tr td { font-size: 0.85rem; }
      `}</style>
      {/* Pagination Controls */}
      {!isLoading && totalPages ? (
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">Rows per page:</span>
            <Select
              value={rowsPerPage.toString()}
              onValueChange={(value) => {
                setRowsPerPage(Number(value));
                setPage(0);
              }}
            >
              <SelectTrigger className="w-[80px]">
                <SelectValue placeholder={rowsPerPage.toString()} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5</SelectItem>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <EnhancedPaginationControl
            totalPages={totalPages}
            currentPage={page}
            onPageChange={(page) => setPage(page)}
          />
          <div className="w-[160px]" /> {/* Spacer to balance layout */}
        </div>
      ) : null}
    </div>
  );
};

export default XpandAccountingEntry;
